import type { ReactNode } from "react";
import { useFieldArray, useFormContext, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Button, FieldMessage } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";

export interface RepeatableListProps {
  /** Ruta del array en el formulario: `"translations.es.benefits"`. */
  name: string;
  /** Lo que se lee en el botón de añadir: "Añadir beneficio". */
  addLabel: string;
  /**
   * Cómo se llama la fila `index` (base 0) para quien no la ve:
   * `(i) => t("Beneficio {{n}}", { n: i + 1 })`.
   *
   * NO es opcional a propósito. "Quitar" a secas, repetido ocho veces en
   * la misma pantalla, es ocho botones idénticos en la lista de un lector
   * de pantalla: no hay forma de saber cuál borra qué.
   */
  itemLabel: (index: number) => string;
  /** El valor de una fila recién añadida. */
  newItem: () => FieldValues;
  /** Cuántas filas admite. El contrato pone el techo real. */
  max?: number;
  /** Qué se lee cuando todavía no hay ninguna fila. */
  emptyHint?: string;
  /**
   * El error de la LISTA ENTERA, no el de una fila: "un paquete necesita
   * al menos un producto".
   *
   * Sin esto no había dónde pintarlo. El contrato pone mínimos sobre el
   * array (`.min(1)`), y react-hook-form deja ese error en la ruta del
   * array —`items`—, que no es el nombre de ningún control: el mensaje
   * existía y no se veía en ninguna parte, así que "Guardar" no hacía
   * nada y no había manera de saber por qué.
   *
   * Es opcional y no cambia nada para quien no lo pasa.
   */
  error?: string;
  /** Los campos de UNA fila. */
  children: (row: { index: number; name: string }) => ReactNode;
  className?: string;
}

/**
 * Una lista de campos que crece: beneficios, pilares, ingredientes,
 * filas de la tabla nutrimental, cifras.
 *
 * ── Por qué `useFieldArray` y no un `useState` con un array ──────────
 * Los valores viven en el formulario, así que sobreviven a cambiar de
 * paso y viajan solos en el envío. Un array en estado local se pierde al
 * desmontar el paso, y copiarlo al formulario a mano es una segunda
 * copia que se desincroniza.
 *
 * Cada fila lleva `id` de react-hook-form como `key`. Con el índice como
 * clave, borrar la fila 2 de cinco haría que React reutilizara los
 * `<input>` corriendo los valores hacia arriba: se borraría la 2 y en
 * pantalla desaparecería la 5.
 *
 * ── Reordenar ────────────────────────────────────────────────────────
 * Con botones de subir y bajar, no arrastrando. Arrastrar no existe para
 * quien navega con teclado y es difícil con motricidad reducida; los dos
 * botones funcionan para todo el mundo y no necesitan librería.
 *
 * Candidato a `shared/ui/fields/`: no sabe nada de productos.
 */
export function RepeatableList({
  name,
  addLabel,
  itemLabel,
  newItem,
  max,
  emptyHint,
  error,
  children,
  className,
}: RepeatableListProps) {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { fields, append, remove, move } = useFieldArray({ control, name });

  const atMax = max !== undefined && fields.length >= max;

  return (
    <div className={cn("grid gap-3", className)}>
      {fields.length === 0 && emptyHint && (
        <p className="text-[13px] leading-relaxed text-ds-ink-3">{emptyHint}</p>
      )}

      {/*
        El error de la lista va ARRIBA, antes de las filas: es lo que
        pasa con el conjunto, y leerlo después de recorrer ocho filas
        llega tarde. `FieldMessage` es el mismo renglón que usan los
        campos —icono, color legible y `aria-live`— para que un error
        del array no se vea distinto de uno de campo.
      */}
      <FieldMessage error={error} />


      <ol className="grid gap-3">
        {fields.map((field, index) => (
          <li
            key={field.id}
            className="grid gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <p className="pt-1 text-[11px] font-semibold uppercase tracking-ds-label-wide text-ds-ink-3">
                {itemLabel(index)}
              </p>

              {/*
                Los tres botones dicen SOBRE QUÉ actúan. El icono es
                decorativo (`aria-hidden` dentro del botón) y el nombre
                accesible completo va en `aria-label`, así que la lista de
                controles de un lector no queda con ocho "Quitar" iguales.
              */}
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t("Subir {{item}}", { item: itemLabel(index) })}
                  disabled={index === 0}
                  onClick={() => move(index, index - 1)}
                >
                  <ArrowUp aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t("Bajar {{item}}", { item: itemLabel(index) })}
                  disabled={index === fields.length - 1}
                  onClick={() => move(index, index + 1)}
                >
                  <ArrowDown aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={t("Quitar {{item}}", { item: itemLabel(index) })}
                  onClick={() => remove(index)}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4 text-ds-terracotta" />
                </Button>
              </div>
            </div>

            {children({ index, name: `${name}.${index}` })}
          </li>
        ))}
      </ol>

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Plus />}
          disabled={atMax}
          onClick={() => append(newItem())}
        >
          {addLabel}
        </Button>
        {atMax && (
          <p className="mt-2 text-[12px] text-ds-ink-3">
            {t("Llegaste al máximo de {{max}}.", { max })}
          </p>
        )}
      </div>
    </div>
  );
}
